import {
  CommunicationRecord,
  GroupAssociation,
  GroupOpportunity,
  OpportunityParticipant,
  Organization,
  Status,
  Team,
  User,
  VolunteerOrganization,
  VolunteerOrgProjectAssignment,
} from "@entities";
import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { JsonField } from "@shared/helpers/custom-fields";
import type { GroupVolunteerRegion } from "@shared/helpers/group-volunteer-regions";
import { Fields, Relations } from "remult";
import { GroupEntityConfig } from "./config";
import { GroupFilters } from "./filters";
import { GroupMethods } from "./methods";

@HyperEntity<Group>("group", GroupEntityConfig)
export class Group extends extender(GroupFilters, GroupMethods) {
  @Fields.id()
  id = "";

  @Relations.toMany(() => GroupAssociation, {
    field: "groupId",
    caption: "רשומים בפועל",
  })
  associations?: GroupAssociation[];

  @Fields.string({ dbName: "admin_id" })
  adminId = "";

  @Relations.toOne<Group, User>(() => User, "adminId")
  admin?: User;

  @Fields.string({ dbName: "team_id" })
  teamId = "";

  @Relations.toOne(() => Team, { caption: "פרויקט", field: "teamId" })
  team?: Team;

  @Relations.toOne(() => Organization, "organizationId")
  organization?: Organization;

  @Fields.string({ dbName: "organization_id" })
  organizationId = "";

  @Fields.string({
    dbName: "volunteer_organization_id",
    allowNull: true,
    caption: "ארגון מתנדבים",
  })
  volunteerOrganizationId?: string | null;

  @Relations.toOne<Group, VolunteerOrganization>(() => VolunteerOrganization, {
    field: "volunteerOrganizationId",
    allowNull: true,
  })
  volunteerOrganization?: VolunteerOrganization;

  @Fields.string({
    dbName: "project_assignment_id",
    allowNull: true,
    caption: "שיבוץ לפרויקט",
  })
  projectAssignmentId?: string | null;

  @Relations.toOne<Group, VolunteerOrgProjectAssignment>(
    () => VolunteerOrgProjectAssignment,
    {
      field: "projectAssignmentId",
      allowNull: true,
    },
  )
  projectAssignment?: VolunteerOrgProjectAssignment;

  @Relations.toMany(() => GroupOpportunity, {
    caption: "משימות",
    field: "groupId",
  })
  opportunities?: GroupOpportunity[];

  @Fields.string({ allowNull: true, dbName: "status_id" })
  statusId = "";

  @Relations.toOne(() => Status, {
    caption: "סטטוס",
    allowNull: true,
    field: "statusId",
  })
  status?: Status;

  @Relations.toMany(() => OpportunityParticipant, {
    field: "group",
    caption: "התעניינות",
    findOptions: {
      where: {
        status: "PENDING",
      },
    },
  })
  interestedOpportunities?: OpportunityParticipant[];

  @Relations.toMany(() => CommunicationRecord, {
    field: "groupId",
    caption: "תיעוד תקשורת",
  })
  communicationRecords?: CommunicationRecord[];

  @Fields.boolean({ dbName: "is_deleted" })
  isDeleted = false;

  @Fields.date({ dbName: "preferred_start_date", caption: "מועד התחלה מועדף" })
  preferredStartDate?: Date;

  @Fields.date({ dbName: "preferred_end_date", caption: "מועד סיום מועדף" })
  preferredEndDate?: Date;

  @Fields.string({ caption: "שם" })
  name = "";

  @Fields.string({ caption: "תיאור" })
  description = "";

  @Fields.string({ caption: "סוג קבוצה", dbName: "type" })
  type = "";

  @Fields.number({ caption: "מספר חברים" })
  size = 0;

  @Fields.string({ caption: "הערות" })
  comments = "";

  @Fields.string({ dbName: "group_coordinator", caption: "מתאם הקבוצה" })
  groupCoordinator = "";

  @Fields.string({ dbName: "transport_mode", caption: "תיאומים נדרשים" })
  transportMode = "";

  @JsonField<Group, GroupVolunteerRegion[]>({
    dbName: "preferred_volunteer_regions",
    caption: "אזורי התנדבות מועדפים",
  })
  preferredVolunteerRegions: GroupVolunteerRegion[] = [];

  @Fields.createdAt<Group>({
    caption: "נוצרה בתאריך",
    dbName: "created_at",
    displayValue(_, value) {
      return value.toLocaleDateString("he");
    },
  })
  createdAt = new Date();

  @Fields.updatedAt({ dbName: "updated_at" })
  updatedAt = new Date();
}
